<template>
  <div class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full"
       style="height: 520px;"
  >
    <div class="relative flex-shrink-0 h-[150px] overflow-hidden rounded-t-2xl bg-gray-200 bg-no-repeat"
         :style="backgroundStyle"
    >
      <ThreeDotMenu
          :challenge="challenge"
          :permissions="permissions"
          @edit="handleEdit(challenge)"
          @toggle-status="toggleStatus"
          @delete="() => $emit('delete', challenge)"
          class="absolute top-0 right-0"
      />
      <div class="absolute bottom-4 left-4">
        <div class="mb-2">
          <span class="px-3 py-1 bg-white text-zinc-800 rounded-full text-sm shadow">
            {{ challenge.type_label }}
          </span>
        </div>
        <div>
          <span class="px-3 py-1 bg-white text-zinc-800 rounded-full text-sm shadow">
            {{ __('Created by {name}', { name: challenge.creator_name }) }}
          </span>
        </div>
      </div>
      <div v-if="isNew" class="absolute top-11 right-0 flex items-center h-6 bg-primary rounded-l-full px-3">
        <Icon name="notifications_unread"  class="w-3.5 h-3.5 text-white" is-filled/>
        <span class="text-sm font-light text-white -ml-0.5">
          {{ __('New') }}
        </span>
      </div>
    </div>

    <div class="p-6 flex flex-col flex-1">
      <span class="text-lg font-medium text-zinc-800 line-clamp-2 min-h-[3.5rem] mb-4 pr-3">
        {{ challenge.title }}
      </span>

      <p class="text-gray-500 text-sm line-clamp-2 min-h-[2rem] mb-6">
        {{ challenge.description || __('No description provided') }}
      </p>

      <div class="flex items-center justify-between mb-6 min-h-[3.5rem]]">
        <div>
          <div class="text-3xl font-medium text-zinc-800">
            {{ submissionCount }}
          </div>
          <div class="text-sm text-gray-500">
            {{ __('Submissions') }}
          </div>
        </div>

        <span
            :class="[
            'px-2.5 py-1 text-base rounded-full font-medium',
            statusClass
          ]"
        >
          {{ statusText }}
        </span>
      </div>

      <div class="mb-6 line-clamp-2 min-h-[30px]">
        <ExpirationBar
            :start-date="challenge.start_at"
            :end-date="challenge.end_at"
            :never-expires="challenge.never_expires"
        />
      </div>

      <button
          @click="reviewNow(challenge)"
          class="mt-auto w-full py-2 px-4 border-2 border-pink-500 text-pink-600 rounded-lg text-sm font-medium hover:bg-pink-50 transition-colors"
      >
        {{ __('Review Now') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ExpirationBar from './ExpirationBar.vue';
import ThreeDotMenu from './ThreeDotMenu.vue';
import {Icon} from "@/components/block/icon";
import {__} from "@/helpers";

const props = defineProps({
  challenge: {
    type: Object,
    required: true
  }
});

const submissionCount = computed(() => props.challenge.submissions_count || 0);
const permissions = computed(() => props.challenge.permissions || {});

const emit = defineEmits(['delete', 'toggle-status', 'edit']);

const handleEdit = (challenge) => {
  window.location.href = route('redesign.company.challenges.edit', { challenge: challenge.id });
};

const redirectToPage = (name, params) => {
  window.location.href = route(name, params);
};

const reviewNow = (challenge) => {
  redirectToPage('redesign.company.challenges.show', { challenge: challenge.id});
};

const isNew = computed(() => {
  const start = new Date(props.challenge.start_at);
  const now = new Date();
  const diffMs = now - start;
  return diffMs >= 0 && diffMs <= 24 * 60 * 60 * 1000;
});

const statusText = computed(() => {
  if (!props.challenge.never_expires && props.challenge.end_at) {
    const endDate = new Date(props.challenge.end_at);
    if (endDate < new Date()) {
      return __('Expired');
    }
  }

  if (!props.challenge.is_active) {
    return __('Inactive');
  }

  const hasSubmissions = props.challenge.submissions_count > 0;
  if (hasSubmissions) {
    const hasPendingReviews = props.challenge.has_pending_reviews ?? true;
    return hasPendingReviews ? __('Pending Review') : __('Evaluated');
  }

  return __('Active');
});

const statusClass = computed(() => {
  const status = statusText.value;

  if (status === __('Inactive')) {
    return 'bg-gray-100 text-gray-700';
  }
  if (status === __('Pending Review')) {
    return 'bg-error bg-opacity-10 text-error';
  }
  if (status === __('Evaluated') || status === __('Expired')) {
    return 'bg-success bg-opacity-10 text-success';
  }
  return 'bg-success bg-opacity-10 text-success';
});

function toggleStatus(challenge) {
  emit('toggle-status', challenge);
}

const svgPositionMap = {
  'object-1.svg': { size: '65%', position: '60% 100%'    },
  'object-2.svg': { size: '95%', position: '-200% -100%'    },
  'object-3.svg': { size: '65%', position: '65% 20%'    },
  'object-4.svg': { size: '80%', position: '60% 0%'    },
  'object-5.svg': { size: '65%', position: '0% 15%'    },
  'object-6.svg': { size: '65%', position: '25% 0%'    },
  'object-7.svg': { size: '45%', position: '100% 50%'    },
  'object-8.svg': { size: '65%', position: '115% 20%'    },

  'default':  { size: '65%', position: '50% 0%'    }
};

const backgroundStyle = computed(() => {
  const url = props.challenge.preview_image_url;
  const isSvg = props.challenge.is_random_preview;

  if (isSvg) {
    const svgName = props.challenge.preview_image
    const svgProperties = svgPositionMap[svgName] || svgPositionMap.default;

    return {
      backgroundImage: `url('${url}')`,
      backgroundSize: svgProperties.size,
      backgroundPosition: svgProperties.position,
      backgroundColor: 'black',
    }
  }
  return {
    backgroundImage: `url('${props.challenge.preview_image_url}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }

});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
